import { create } from 'zustand';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import type { DatePreset } from '@/types/date';
import type { SavedReport, AnalyticsPageType } from '../types/analytics';

interface AnalyticsFilterState {
  /** Currently active analytics page */
  pageType: AnalyticsPageType;
  /** Date range start */
  dateFrom: Date;
  /** Date range end */
  dateTo: Date;
  /** Active date preset */
  datePreset: DatePreset;
  /** Asset tag filters (Proof of Play only) */
  assetTags: string[];
  /** Screen tag filters (Proof of Play only) */
  screenTags: string[];
  /** Currently loaded saved report (null = default/unsaved) */
  activeReport: SavedReport | null;
  /** Whether there are unsaved changes to the active report */
  hasUnsavedChanges: boolean;
  /** Whether the tag filter panel is open */
  isFilterPanelOpen: boolean;

  setPageType: (pageType: AnalyticsPageType) => void;
  setDateRange: (from: Date, to: Date, preset: DatePreset) => void;
  setAssetTags: (tags: string[]) => void;
  setScreenTags: (tags: string[]) => void;
  setActiveReport: (report: SavedReport | null) => void;
  markUnsavedChanges: () => void;
  clearUnsavedChanges: () => void;
  toggleFilterPanel: () => void;
  clearFilters: () => void;
}

function getDefaultDates() {
  return {
    dateFrom: startOfDay(subDays(new Date(), 6)),
    dateTo: endOfDay(new Date()),
    datePreset: 'last7days' as DatePreset,
  };
}

export const useAnalyticsFilterStore = create<AnalyticsFilterState>((set) => ({
  ...getDefaultDates(),
  pageType: 'proofOfPlay',
  assetTags: [],
  screenTags: [],
  activeReport: null,
  hasUnsavedChanges: false,
  isFilterPanelOpen: false,

  setPageType: (pageType) =>
    set((s) => ({
      pageType,
      // Clear active report when switching between POP and AI Analytics
      ...(s.pageType !== pageType ? { activeReport: null, hasUnsavedChanges: false } : {}),
    })),

  setDateRange: (dateFrom, dateTo, datePreset) =>
    set({ dateFrom, dateTo, datePreset, hasUnsavedChanges: true }),

  setAssetTags: (assetTags) => set({ assetTags, hasUnsavedChanges: true }),

  setScreenTags: (screenTags) => set({ screenTags, hasUnsavedChanges: true }),

  setActiveReport: (activeReport) =>
    set({
      activeReport,
      hasUnsavedChanges: false,
      // Restore report's saved filters when opening
      ...(activeReport
        ? {
            dateFrom: startOfDay(new Date(activeReport.startDate)),
            dateTo: endOfDay(new Date(activeReport.endDate)),
            datePreset: 'custom' as DatePreset,
            assetTags: activeReport.assetTags,
            screenTags: activeReport.screenTags,
          }
        : {}),
    }),

  markUnsavedChanges: () => set({ hasUnsavedChanges: true }),
  clearUnsavedChanges: () => set({ hasUnsavedChanges: false }),
  toggleFilterPanel: () => set((s) => ({ isFilterPanelOpen: !s.isFilterPanelOpen })),

  clearFilters: () =>
    set({
      ...getDefaultDates(),
      assetTags: [],
      screenTags: [],
      activeReport: null,
      hasUnsavedChanges: false,
    }),
}));
